import { getScopedDomain } from '../lib/tenantScope';
// Admin Alerts — operator inbox for cert expiry, firmware advisories, SBC offline,
// reboot failures, and other admin_notifications categories.

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell, CheckCheck, X, AlertTriangle, AlertCircle, Info, Zap } from 'lucide-react';
import { api } from '../lib/api';

const SEVERITY_ORDER = ['critical', 'error', 'warn', 'info'];

export default function AdminAlerts() {
  const me = api.me();
  const qc = useQueryClient();
  const [status, setStatus] = useState<'unread' | 'acknowledged' | 'dismissed' | ''>('unread');

  const { data: list = [] } = useQuery({
    queryKey: ['alerts', getScopedDomain(me?.domain_uuid), status],
    queryFn: () => api.adminNotifications.list({ domain_uuid: getScopedDomain(me?.domain_uuid), status, limit: 200 }),
  });

  const { data: counts } = useQuery({
    queryKey: ['alerts-counts', getScopedDomain(me?.domain_uuid)],
    queryFn: () => api.adminNotifications.counts(getScopedDomain(me?.domain_uuid)),
    refetchInterval: 60_000,
  });

  const ack = useMutation({
    mutationFn: (id: number) => api.adminNotifications.acknowledge(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['alerts'] }) && qc.invalidateQueries({ queryKey: ['alerts-counts'] }),
  });
  const dismiss = useMutation({
    mutationFn: (id: number) => api.adminNotifications.dismiss(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['alerts'] }) && qc.invalidateQueries({ queryKey: ['alerts-counts'] }),
  });
  const ackAll = useMutation({
    mutationFn: () => api.adminNotifications.acknowledgeAll({ domain_uuid: getScopedDomain(me?.domain_uuid) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['alerts'] }) && qc.invalidateQueries({ queryKey: ['alerts-counts'] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold flex items-center gap-2"><Bell /> Admin Alerts</h1>
        <button className="btn" onClick={() => ackAll.mutate()} disabled={ackAll.isPending}>
          <CheckCheck size={14} className="inline mr-1" /> Acknowledge all
        </button>
      </div>

      {counts && (
        <div className="card p-3 grid grid-cols-4 gap-3 text-sm">
          <Stat severity="critical" n={counts.critical} />
          <Stat severity="error" n={counts.error} />
          <Stat severity="warn" n={counts.warn} />
          <Stat severity="info" n={counts.info} />
        </div>
      )}

      <div className="flex gap-1">
        {(['unread', 'acknowledged', 'dismissed'] as const).map(s => (
          <button key={s} onClick={() => setStatus(s)}
            className={`btn capitalize ${status === s ? 'btn-primary' : ''}`}>{s}</button>
        ))}
      </div>

      <div className="card p-0 overflow-hidden">
        <table className="table">
          <thead><tr><th></th><th>Title</th><th>Category</th><th>When</th><th></th></tr></thead>
          <tbody>
            {[...list].sort((a, b) => SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity)).map((n: any) => (
              <tr key={n.notification_id} className="hover:bg-elevated">
                <td><SeverityIcon s={n.severity} /></td>
                <td>
                  <div className="font-medium">{n.title}</div>
                  {n.body && <div className="text-xs text-textSecondary mt-0.5 max-w-md">{n.body}</div>}
                </td>
                <td><span className="badge">{n.category}</span></td>
                <td className="text-xs">{new Date(n.created_at).toLocaleString()}</td>
                <td className="space-x-1 whitespace-nowrap">
                  {!n.acknowledged_at && <button className="btn-ghost p-1" title="Acknowledge" onClick={() => ack.mutate(n.notification_id)}><CheckCheck size={14} /></button>}
                  {!n.dismissed_at && <button className="btn-ghost p-1 text-danger" title="Dismiss" onClick={() => dismiss.mutate(n.notification_id)}><X size={14} /></button>}
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-textSecondary">No alerts in this view. Quiet skies. 🌤</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SeverityIcon({ s }: { s: string }) {
  if (s === 'critical') return <Zap size={16} className="text-danger" />;
  if (s === 'error')    return <AlertTriangle size={16} className="text-danger" />;
  if (s === 'warn')     return <AlertCircle size={16} className="text-warning" />;
  return <Info size={16} className="text-textSecondary" />;
}

function Stat({ severity, n }: { severity: string; n: number }) {
  const cls = severity === 'critical' ? 'text-danger'
            : severity === 'error'    ? 'text-danger'
            : severity === 'warn'     ? 'text-warning'
            : 'text-textSecondary';
  return (
    <div className="flex items-center gap-2">
      <SeverityIcon s={severity} />
      <span className={`text-2xl font-semibold ${cls}`}>{n}</span>
      <span className="text-xs text-textSecondary capitalize">{severity}</span>
    </div>
  );
}
